import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Download,
  Upload,
  Database,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  FileJson,
  ShieldAlert,
} from "lucide-react";

const BackupRestoreChild = () => {
  const [activeTab, setActiveTab] = useState("export");
  const [collections, setCollections] = useState([]);
  const [totalDbRecords, setTotalDbRecords] = useState(0);
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);

  // Export State
  const [selectedForExport, setSelectedForExport] = useState([]);
  const [includeUsers, setIncludeUsers] = useState(false);
  const [includePasswords, setIncludePasswords] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Import / Restore State
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedBackupData, setParsedBackupData] = useState(null);
  const [inspectionResult, setInspectionResult] = useState(null);
  const [isInspecting, setIsInspecting] = useState(false);
  const [selectedForRestore, setSelectedForRestore] = useState([]);
  const [restoreMode, setRestoreMode] = useState("merge"); // "merge" or "clean"
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreSummary, setRestoreSummary] = useState(null);
  const [confirmCleanText, setConfirmCleanText] = useState("");

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL;

  // Comprehensive error message extractor
  const extractErrorMessage = (error, fallback = "Operation failed.") => {
    if (error?.response?.data) {
      const data = error.response.data;
      if (typeof data === "string") return data;
      if (data.error) return typeof data.error === "string" ? data.error : JSON.stringify(data.error);
      if (data.message) return data.message;
    }
    if (error?.response?.status === 413) {
      return "The backup file is too large to process. Please try a smaller file or subset.";
    }
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      return "Access denied. Administrator privileges required or session expired.";
    }
    if (error?.message) {
      return error.message;
    }
    return fallback;
  };

  // Fetch available collections and stats
  const fetchCollectionStats = async () => {
    setIsLoadingCollections(true);
    try {
      const response = await axios.get(`${API_URL}/backup/collections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.collections) {
        setCollections(response.data.collections);
        setTotalDbRecords(response.data.totalRecords || 0);
        // Default select all non-user collections for export
        setSelectedForExport(
          response.data.collections
            .filter((c) => c.id !== "users")
            .map((c) => c.id)
        );
      }
    } catch (error) {
      console.error("Failed to load collection stats:", error);
      toast.error(extractErrorMessage(error, "Failed to load database collections."));
    } finally {
      setIsLoadingCollections(false);
    }
  };

  useEffect(() => {
    fetchCollectionStats();
  }, []);

  // Handle Export Checkbox Toggles
  const toggleExportCollection = (id) => {
    if (selectedForExport.includes(id)) {
      setSelectedForExport(selectedForExport.filter((item) => item !== id));
    } else {
      setSelectedForExport([...selectedForExport, id]);
    }
  };

  const selectAllExport = () => {
    setSelectedForExport(collections.map((c) => c.id));
    setIncludeUsers(true);
  };

  const deselectAllExport = () => {
    setSelectedForExport([]);
    setIncludeUsers(false);
  };

  // Trigger Export Download
  const handleExport = async () => {
    if (selectedForExport.length === 0 && !includeUsers) {
      return toast.error("Please select at least one collection to export.");
    }

    setIsExporting(true);
    try {
      const payload = {
        selectedCollections: selectedForExport,
        includeUsers,
        includePasswords,
      };

      const response = await axios.post(`${API_URL}/backup/export`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
        JSON.stringify(data, null, 2)
      )}`;

      const dateStr = new Date().toISOString().slice(0, 10);
      const timeStr = new Date().toTimeString().slice(0, 5).replace(":", "-");
      const filename = `donosure_backup_${dateStr}_${timeStr}.json`;

      const downloadAnchor = document.createElement("a");
      downloadAnchor.setAttribute("href", jsonString);
      downloadAnchor.setAttribute("download", filename);
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();

      toast.success(`Export successful! Downloaded ${data.totalRecords} records.`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error(extractErrorMessage(error, "Export failed."));
    } finally {
      setIsExporting(false);
    }
  };

  // Handle File Upload for Import
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".json")) {
      return toast.error("Please select a valid .json backup file.");
    }

    setUploadedFile(file);
    setRestoreSummary(null);
    setInspectionResult(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const rawText = event.target.result.trim();
        let parsed;
        try {
          parsed = JSON.parse(rawText);
        } catch (jsonErr) {
          // Attempt newline-delimited JSON (ndjson / json lines)
          if (rawText.includes("\n")) {
            const lines = rawText
              .split("\n")
              .map((l) => l.trim())
              .filter(Boolean);
            parsed = lines.map((l) => JSON.parse(l));
          } else {
            throw jsonErr;
          }
        }
        setParsedBackupData(parsed);
        inspectBackup(parsed);
      } catch (err) {
        console.error("JSON parse error:", err);
        toast.error("Failed to parse JSON file: " + (err.message || "Ensure the file contains valid JSON."));
        setParsedBackupData(null);
      }
    };
    reader.readAsText(file);

    // Reset file input value so re-selecting same file fires change event
    e.target.value = "";
  };

  // Inspect Backup Payload via Backend
  const inspectBackup = async (data) => {
    setIsInspecting(true);
    try {
      const response = await axios.post(
        `${API_URL}/backup/inspect`,
        { backupData: data },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInspectionResult(response.data);
      // Default select all collections found in backup
      if (response.data?.collections) {
        setSelectedForRestore(response.data.collections.map((c) => c.id));
      }
      toast.success("Backup file verified successfully.");
    } catch (error) {
      console.error("Inspection failed:", error);
      toast.error(extractErrorMessage(error, "Invalid backup format."));
      setInspectionResult(null);
    } finally {
      setIsInspecting(false);
    }
  };

  const toggleRestoreCollection = (id) => {
    if (selectedForRestore.includes(id)) {
      setSelectedForRestore(selectedForRestore.filter((item) => item !== id));
    } else {
      setSelectedForRestore([...selectedForRestore, id]);
    }
  };

  // Execute Restore
  const handleRestore = async () => {
    if (!parsedBackupData) {
      return toast.error("Please upload and inspect a backup file first.");
    }

    if (selectedForRestore.length === 0) {
      return toast.error("Please select at least one collection to restore.");
    }

    if (restoreMode === "clean" && confirmCleanText.trim().toUpperCase() !== "RESTORE") {
      return toast.error('Type "RESTORE" to confirm clean overwrite.');
    }

    setIsRestoring(true);
    try {
      const payload = {
        backupData: inspectionResult?.normalizedData || parsedBackupData,
        mode: restoreMode,
        selectedCollections: selectedForRestore,
        preserveCurrentUser: true,
      };

      const response = await axios.post(`${API_URL}/backup/restore`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setRestoreSummary(response.data);
      toast.success(`Restore completed! Processed ${response.data.totalProcessed} records.`);
      fetchCollectionStats(); // Refresh database counts
      setConfirmCleanText("");
    } catch (error) {
      console.error("Restore failed:", error);
      toast.error(extractErrorMessage(error, "Restore execution failed."));
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div className="w-full p-4 md:p-6 bg-gray-50 min-h-screen">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between p-6 mb-6 bg-white rounded-xl shadow-sm border border-gray-200">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-lg">
              <Database className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Database Backup & Restore</h1>
              <p className="text-sm text-gray-500">
                Export comprehensive JSON snapshots for offline backup or restore database state across environments.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-4 md:mt-0 flex items-center gap-3">
          <button
            onClick={fetchCollectionStats}
            disabled={isLoadingCollections}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            title="Refresh database statistics"
          >
            <RefreshCw className={`w-4 h-4 ${isLoadingCollections ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs Bar */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab("export")}
          className={`flex items-center gap-2 py-3 px-6 font-semibold text-sm transition-all border-b-2 ${
            activeTab === "export"
              ? "border-indigo-600 text-indigo-600 bg-white rounded-t-lg shadow-sm"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <Download className="w-4 h-4" />
          Export Database (Backup)
        </button>
        <button
          onClick={() => setActiveTab("import")}
          className={`flex items-center gap-2 py-3 px-6 font-semibold text-sm transition-all border-b-2 ${
            activeTab === "import"
              ? "border-indigo-600 text-indigo-600 bg-white rounded-t-lg shadow-sm"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <Upload className="w-4 h-4" />
          Import Database (Restore)
        </button>
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: EXPORT & BACKUP                                                    */}
      {/* ========================================================================= */}
      {activeTab === "export" && (
        <div className="space-y-6">
          {/* Quick Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Total Records</span>
              <p className="text-2xl font-bold text-gray-800 mt-1">{totalDbRecords.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Registered Collections</span>
              <p className="text-2xl font-bold text-gray-800 mt-1">{collections.length}</p>
            </div>
            <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Selected for Export</span>
              <p className="text-2xl font-bold text-indigo-600 mt-1">
                {selectedForExport.length + (includeUsers ? 1 : 0)} collections
              </p>
            </div>
          </div>

          {/* Export Settings Card */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between pb-4 mb-4 border-b border-gray-100 gap-4">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Select Collections to Include</h2>
                <p className="text-xs text-gray-500">Choose which database collections you want to package into the JSON archive.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllExport}
                  className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={deselectAllExport}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition"
                >
                  Deselect All
                </button>
              </div>
            </div>

            {/* Collection Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              {collections
                .filter((c) => c.id !== "users")
                .map((col) => {
                  const isChecked = selectedForExport.includes(col.id);
                  return (
                    <div
                      key={col.id}
                      onClick={() => toggleExportCollection(col.id)}
                      className={`flex items-start p-3 rounded-lg border cursor-pointer transition-all ${
                        isChecked
                          ? "bg-indigo-50/50 border-indigo-300 shadow-xs"
                          : "bg-white border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {}}
                        className="mt-1 h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                      />
                      <div className="ml-3 flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-900 truncate">{col.label}</p>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {col.count}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{col.description}</p>
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* User Collection Security Options */}
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 mb-6">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-amber-900">User Accounts & Authentication Data</h3>
                  <p className="text-xs text-amber-700 mt-1">
                    User records contain consultant/admin profiles. By default, password hashes are stripped for safety.
                  </p>
                  <div className="mt-3 flex flex-col sm:flex-row gap-4">
                    <label className="flex items-center text-xs font-medium text-amber-900 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeUsers}
                        onChange={(e) => setIncludeUsers(e.target.checked)}
                        className="h-4 w-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500 mr-2"
                      />
                      Include Users Collection ({collections.find((c) => c.id === "users")?.count || 0} users)
                    </label>
                    {includeUsers && (
                      <label className="flex items-center text-xs font-medium text-red-800 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includePasswords}
                          onChange={(e) => setIncludePasswords(e.target.checked)}
                          className="h-4 w-4 text-red-600 rounded border-red-300 focus:ring-red-500 mr-2"
                        />
                        Include Password Hashes (Full Disaster Recovery Only)
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Export Trigger Action */}
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={handleExport}
                disabled={isExporting}
                className="flex items-center gap-2 px-6 py-3 text-white font-semibold bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50"
              >
                {isExporting ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Generating Export Package...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Download JSON Backup
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: IMPORT & RESTORE                                                    */}
      {/* ========================================================================= */}
      {activeTab === "import" && (
        <div className="space-y-6">
          {/* File Upload Zone */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <h2 className="text-lg font-bold text-gray-800 mb-2">Upload Backup Archive (.json)</h2>
            <p className="text-xs text-gray-500 mb-4">
              Select a valid DonoSure JSON backup file to inspect its contents prior to applying restoration.
            </p>

            <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-indigo-400 transition bg-gray-50/50">
              <div className="space-y-2 text-center">
                <FileJson className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600 justify-center">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500 px-2 py-1 shadow-xs border"
                  >
                    <span>Browse File</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      accept=".json,application/json"
                      onChange={handleFileUpload}
                      className="sr-only"
                    />
                  </label>
                  <p className="pl-1 pt-1 text-xs text-gray-500">or drag and drop here</p>
                </div>
                {uploadedFile && (
                  <p className="text-xs font-semibold text-emerald-600">
                    Selected: {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Pre-Restore Inspection Preview */}
          {isInspecting && (
            <div className="p-8 bg-white rounded-xl border text-center">
              <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-600">Verifying backup schema and record counts...</p>
            </div>
          )}

          {inspectionResult && (
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between pb-4 border-b border-gray-100 gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-lg font-bold text-gray-900">Backup Inspection Verified</h3>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Exported on: {new Date(inspectionResult.exportedAt).toLocaleString()} by{" "}
                    <span className="font-medium text-gray-700">{inspectionResult.exportedBy?.name || "Admin"}</span>
                  </p>
                </div>
                <span className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-full">
                  {inspectionResult.totalRecordsInBackup} Records in Archive
                </span>
              </div>

              {/* Collections Selection Table */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Select Collections to Restore:</h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Restore?</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Collection</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-500">Records in Backup</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-500">Current in Database</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {inspectionResult.collections.map((col) => {
                        const isSelected = selectedForRestore.includes(col.id);
                        return (
                          <tr key={col.id} className={isSelected ? "bg-indigo-50/20" : ""}>
                            <td className="px-4 py-2.5">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleRestoreCollection(col.id)}
                                className="h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                              />
                            </td>
                            <td className="px-4 py-2.5 font-medium text-gray-900">{col.label}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-indigo-600">
                              {col.backupCount}
                            </td>
                            <td className="px-4 py-2.5 text-right text-gray-500">{col.currentDbCount}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Restore Mode Configuration */}
              <div className="space-y-3 pt-2">
                <h4 className="text-sm font-semibold text-gray-800">Choose Restoration Strategy:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    onClick={() => setRestoreMode("merge")}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      restoreMode === "merge"
                        ? "border-indigo-600 bg-indigo-50/40 ring-1 ring-indigo-600"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="restoreMode"
                        checked={restoreMode === "merge"}
                        onChange={() => setRestoreMode("merge")}
                        className="text-indigo-600"
                      />
                      <span className="font-bold text-sm text-gray-900">Merge / Upsert (Safe Mode)</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Updates existing records by ID and adds new ones. Does <strong>not</strong> delete records that
                      are currently in the database.
                    </p>
                  </div>

                  <div
                    onClick={() => setRestoreMode("clean")}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      restoreMode === "clean"
                        ? "border-red-600 bg-red-50/40 ring-1 ring-red-600"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="restoreMode"
                        checked={restoreMode === "clean"}
                        onChange={() => setRestoreMode("clean")}
                        className="text-red-600"
                      />
                      <span className="font-bold text-sm text-red-900">Clean Overwrite (Full Reset)</span>
                    </div>
                    <p className="text-xs text-red-600 mt-2">
                      Wipes the selected collections before importing the backup. Current admin user is preserved.
                    </p>
                  </div>
                </div>
              </div>

              {/* Confirmation required for Clean Overwrite */}
              {restoreMode === "clean" && (
                <div className="p-4 bg-red-50 rounded-xl border border-red-200 space-y-3">
                  <div className="flex items-center gap-2 text-red-800 text-sm font-bold">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Danger Zone: Clean Overwrite Confirmation
                  </div>
                  <p className="text-xs text-red-700">
                    This operation will delete current records in the selected collections before restoring. Type{" "}
                    <strong>RESTORE</strong> below to enable the execution button:
                  </p>
                  <input
                    type="text"
                    value={confirmCleanText}
                    onChange={(e) => setConfirmCleanText(e.target.value)}
                    placeholder='Type "RESTORE" to confirm'
                    className="w-full max-w-sm px-3 py-2 text-sm border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              )}

              {/* Execute Action */}
              <div className="flex justify-end pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleRestore}
                  disabled={
                    isRestoring ||
                    selectedForRestore.length === 0 ||
                    (restoreMode === "clean" && confirmCleanText.trim().toUpperCase() !== "RESTORE")
                  }
                  className={`flex items-center gap-2 px-6 py-3 text-white font-semibold rounded-lg shadow-md transition-all ${
                    restoreMode === "clean"
                      ? "bg-red-600 hover:bg-red-700 disabled:opacity-40"
                      : "bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                  }`}
                >
                  {isRestoring ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Executing Restoration...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      {restoreMode === "clean" ? "Wipe & Restore Database" : "Merge & Restore Database"}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Post-Restore Summary Display */}
          {restoreSummary && (
            <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-xl space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                <h3 className="text-lg font-bold text-emerald-900">{restoreSummary.message}</h3>
              </div>
              <p className="text-xs text-emerald-700">
                Total records processed: <strong>{restoreSummary.totalProcessed}</strong> (Mode:{" "}
                <span className="uppercase font-semibold">{restoreSummary.mode}</span>)
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
                {Object.entries(restoreSummary.results || {}).map(([key, stat]) => (
                  <div key={key} className="bg-white p-3 rounded-lg border border-emerald-200 text-xs">
                    <p className="font-bold text-gray-800">{stat.label}</p>
                    <div className="mt-1 flex justify-between text-gray-500">
                      <span>Inserted/Upserted:</span>
                      <span className="font-semibold text-emerald-600">{stat.inserted}</span>
                    </div>
                    <div className="flex justify-between text-gray-500">
                      <span>Updated:</span>
                      <span className="font-semibold text-indigo-600">{stat.updated}</span>
                    </div>
                    {stat.deletedBefore > 0 && (
                      <div className="flex justify-between text-red-500">
                        <span>Cleared:</span>
                        <span className="font-semibold">{stat.deletedBefore}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BackupRestoreChild;
